<template>
  <div class="ai-logo-generator">
    <!-- 头部导航 - 固定在顶部 -->
    <div class="header-fixed">
      <HeaderNav ref="headerNavRef" @login-click="showLogin = true" @nav-click="handleNavClick" @logout="handleLogout" @history="handleHistory" />
    </div>
        <LoginModal v-model:visible="showLogin" @login-success="handleLoginSuccess" @update:visible="handleLoginModalUpdate" />
    
    <!-- 预览弹窗 -->
    <LogoPreviewModal
      :visible="showPreviewModal"
      :logo="currentPreviewLogo"
      :examples="previewExamples"
      :loading="previewLoading"
      :user-info="userInfo"
      @close="closePreviewModal"
      @download="handlePreviewDownload"
      @show-vip-modal="showVipModal = true"
    />

    <!-- 下载格式选择对话框 -->
    <DownloadFormatDialog
      :visible="showDownloadDialog"
      :user-info="userInfo"
      :logo-data="currentDownloadLogo"
      @close="handleDownloadDialogClose"
      @confirm="handleDownloadFormatConfirm"
      @show-vip-modal="showVipModal = true"
    />

    <!-- VIP弹窗 -->
    <VipModal 
      v-model="showVipModal" 
      :user-info="userInfo"
      @confirm="handleVipConfirm"
      @updateUserInfo="handleUpdateUserInfo"
    />

    <!-- 设计方案选择弹窗 -->
    <div v-if="showSolutionModal" class="solution-modal-overlay" @click="showSolutionModal = false">
      <div class="solution-modal" @click.stop>
        <div class="solution-modal-header">
          <h3 class="solution-modal-title">选择设计方案</h3>
          <button class="solution-modal-close" @click="showSolutionModal = false">
            <Close />
          </button>
        </div>
        <div class="solution-modal-content">
          <div class="solution-list">
            <div 
              v-for="(solution, index) in designSolutions" 
              :key="solution.solution_id || index"
              class="solution-item"
              :class="{ active: selectedSolutionIndex === index }"
              @click="selectSolution(index)"
            >
              <div class="solution-header">
                <h4 class="solution-name">{{ solution.name }}</h4>
                <div class="solution-badge" v-if="selectedSolutionIndex === index">已选择</div>
              </div>
              <p class="solution-description">{{ solution.description }}</p>
              
              <div class="solution-details">
                <div class="solution-colors" v-if="solution.color_scheme && solution.color_scheme.length > 0">
                  <span class="detail-label">配色方案：</span>
                  <div class="color-tags">
                    <span 
                      v-for="(color, i) in solution.color_scheme" 
                      :key="i"
                      class="color-tag"
                    >
                      {{ color }}
                    </span>
                  </div>
                </div>
                
                <div class="solution-graphics" v-if="solution.graphics && solution.graphics.length > 0">
                  <span class="detail-label">图形元素：</span>
                  <ul class="graphics-list">
                    <li v-for="(graphic, i) in solution.graphics" :key="i">{{ graphic }}</li>
                  </ul>
                </div>
                
                <div class="solution-typography" v-if="solution.typography">
                  <span class="detail-label">字体设计：</span>
                  <div class="typography-info">
                    <span>{{ solution.typography.font_style }}</span>
                    <span v-if="solution.typography.color">颜色：{{ solution.typography.color }}</span>
                  </div>
                </div>
                
                <div class="solution-elements" v-if="solution.key_elements && solution.key_elements.length > 0">
                  <span class="detail-label">关键元素：</span>
                  <div class="elements-tags">
                    <span 
                      v-for="(element, i) in solution.key_elements" 
                      :key="i"
                      class="element-tag"
                    >
                      {{ element }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="solution-modal-footer">
          <button class="solution-btn-cancel" @click="showSolutionModal = false">取消</button>
          <button class="solution-btn-confirm" @click="confirmSolution">确认选择</button>
        </div>
      </div>
    </div>

    <!-- 免责声明弹窗 -->
    <div v-if="showDisclaimerModal" class="disclaimer-modal-overlay" @click="showDisclaimerModal = false">
      <div class="disclaimer-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">免责声明</h3>
          <button class="modal-close" @click="showDisclaimerModal = false">
            <Close />
          </button>
        </div>
        <div class="modal-content">
          <div class="disclaimer-content">
            <p>使用本AI Logo生成服务，即表示您同意以下条款：</p>
            
            <h4>1. 服务说明</h4>
            <p>本服务使用人工智能技术生成LOGO设计，生成的内容仅供参考，不保证完全符合您的具体需求。</p>
            
            <h4>2. 知识产权</h4>
            <p>您对生成的LOGO享有使用权，但请确保：</p>
            <ul>
              <li>不侵犯他人知识产权</li>
              <li>不违反相关法律法规</li>
              <li>不用于违法用途</li>
            </ul>
            
            <h4>3. 免责条款</h4>
            <p>我们不对以下情况承担责任：</p>
            <ul>
              <li>AI生成内容的质量和适用性</li>
              <li>因使用生成内容而产生的任何损失</li>
              <li>第三方对生成内容的争议</li>
            </ul>
            
            <h4>4. 隐私保护</h4>
            <p>我们重视您的隐私，会按照隐私政策保护您的个人信息。</p>
            
            <p class="disclaimer-footer">
              继续使用本服务即表示您已阅读并同意以上条款。
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="confirm-btn" @click="showDisclaimerModal = false">
            我已了解
          </button>
        </div>
      </div>
    </div>

    <!-- 行业选择悬浮窗 -->
    <div v-if="showIndustryModal" class="industry-modal-overlay" @click="showIndustryModal = false">
      <div class="industry-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">选择行业</h3>
          <button class="modal-close" @click="showIndustryModal = false">
            <Close />
          </button>
        </div>
        <div class="modal-content">
          <div class="industry-grid">
            <div 
              v-for="industry in industries" 
              :key="industry.id"
              class="industry-item"
              :class="{ active: formData.industry === industry.id }"
              @click="toggleIndustry(industry.id)"
            >
              <div class="industry-icon">
                <img :src="industry.icon" :alt="industry.name" />
              </div>
              <span class="industry-name">{{ industry.name }}</span>
              <div v-if="formData.industry === industry.id" class="check-icon">
                <Check />
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="confirm-btn" @click="showIndustryModal = false">
            确定{{ formData.industry ? ' (已选择)' : '' }}
          </button>
        </div>
      </div>
    </div>

    <div class="main-container">
      <!-- 左侧输入区域 -->
      <div class="left-panel">
        <div class="input-section">
          <!-- 选择生成模型 - 移到顶部 -->
      
          <div class="model-selection">
            <h4 class="section-title">选择生成模型</h4>
            <div class="model-cards">
              <div 
                v-for="model in models" 
                :key="model.id"
                class="model-card"
                :class="{ 
                  active: selectedModel === model.id,
                  disabled: model.disabled 
                }"
                @click="!model.disabled && (selectedModel = model.id)"
              >
                <div class="model-badge" :class="model.type">{{ model.badge }}</div>
                <h5 class="model-name">{{ model.name }}</h5>
                <p class="model-desc">{{ model.description }}</p>
              </div>
            </div>
          </div>

          <!-- LOGO名称 -->
          <div class="input-group">
            <label class="input-label">LOGO名称（必填）：</label>
            <div class="input-wrapper">
              <input 
                v-model="formData.brandName" 
                type="text" 
                placeholder="请输入您的公司名称"
                maxlength="20"
                class="brand-input"
              />
              <div class="char-count">{{ formData.brandName.length }}/15</div>
            </div>
          </div>

          <!-- 副标题/口号 -->
          <div class="input-group">
            <label class="input-label">副标题/口号：</label>
            <div class="input-wrapper">
              <input 
                v-model="formData.slogan" 
                type="text" 
                placeholder="请输入您的副标题/口号"
                maxlength="30"
                class="slogan-input"
              />
              <div class="char-count">{{ formData.slogan.length }}/20</div>
            </div>
          </div>

          <!-- 行业选择 -->
          <div class="input-group">
            <label class="input-label">请选择行业：</label>
            <div class="industry-selection">
              <div class="industry-tags">
                <div 
                  v-for="industry in industries.slice(0, displayedIndustriesCount)" 
                  :key="industry.id"
                  class="industry-tag"
                  :class="{ active: formData.industry === industry.id }"
                  @click="toggleIndustry(industry.id)"
                >
                  <img :src="industry.icon" :alt="industry.name" class="industry-icon" />
                  <span>{{ industry.name }}</span>
                </div>
                
                <!-- 更多按钮 -->
                <div 
                  class="industry-tag more-btn"
                  @click="showIndustryModal = true"
                >
                  <More />
                  <span>更多</span>
                </div>
              </div>
            </div>
          </div>
 
          <!-- 高级设置 -->
           
          <div class="advanced-settings">

            
            <!-- LOGO类型选择 -->
              <!--
            <div class="setting-group">
              <div class="setting-tabs">
                <button 
                  class="tab-btn"
                  :class="{ active: logoType === 'graphic' }"
                  @click="logoType = 'graphic'"
                >
                  图形LOGO
                </button>
                <button 
                  class="tab-btn"
                  :class="{ active: logoType === 'text' }"
                  @click="logoType = 'text'"
                >
                  字体/数字LOGO
                </button>
              </div>
            </div>
          -->
            <!-- 文字描述 -->
            <div class="setting-group">
              <div class="setting-title-wrapper">
                <h4 class="setting-title">用文字描述您的LOGO</h4>
                <button 
                  class="generate-desc-btn" 
                  @click="generateLogoDescription"
                  :disabled="!formData.brandName || !formData.industry || isGeneratingDesc"
                >
                  <span v-if="isGeneratingDesc" class="generating-wrapper">
                    <span class="btn-loading-spinner"></span>
                    <span>生成中...</span>
                  </span>
                  <span v-else class="generate-desc-content">
                    <img src="@/assets/static/img/ai.png" alt="AI" class="generate-desc-icon" />
                    <span>生成图标描述</span>
                  </span>
                </button>
              </div>
              <div class="textarea-wrapper">
                <textarea 
                  v-model="formData.description"
                  placeholder="请描述您的灵感、例如：简洁、现代、小清、充满活力"
                  class="description-textarea"
                  rows="4"
                  maxlength="100"
                ></textarea>
                <div class="char-count">{{ formData.description.length }}/100</div>
              </div>
            </div>

            <!-- LOGO风格 -->
            <div class="setting-group">
              <h4 class="setting-title">您喜欢的LOGO风格是？</h4>
              <div class="style-list">
                <div 
                  class="style-item"
                  :class="{ active: formData.style === 'pixel' }"
                  @click="formData.style = 'pixel'"
                >
                  <div class="style-wrap">
                    <img src="@/assets/static/images/logo/pixel.png" alt="像素风" />
                    <div class="style-name">像素风</div>
                  </div>
                </div>
                
                <div 
                  class="style-item"
                  :class="{ active: formData.style === '故障艺术' }"
                  @click="formData.style = '故障艺术'"
                >
                  <div class="style-wrap">
                    <img src="@/assets/static/images/logo/fault.png" alt="故障艺术" />
                    <div class="style-name">故障艺术</div>
                  </div>
                </div>
                
                <div 
                  class="style-item"
                  :class="{ active: formData.style === '复古' }"
                  @click="formData.style = '复古'"
                >
                  <div class="style-wrap">
                    <img src="@/assets/static/images/logo/retro.png" alt="复古" />
                    <div class="style-name">复古</div>
                  </div>
                </div>
                
                <div 
                  class="style-item"
                  :class="{ active: formData.style === '简约' }"
                  @click="formData.style = '简约'"
                >
                  <div class="style-wrap">
                    <img src="@/assets/static/images/logo/simple.png" alt="简约" />
                    <div class="style-name">简约</div>
                  </div>
                </div>
                
                <div 
                  class="style-item"
                  :class="{ active: formData.style === '扁平' }"
                  @click="formData.style = '扁平'"
                >
                  <div class="style-wrap">
                    <img src="@/assets/static/images/logo/flat.png" alt="扁平" />
                    <div class="style-name">扁平</div>
                  </div>
                </div>
                
                <div 
                  class="style-item"
                  :class="{ active: formData.style === '卡通' }"
                  @click="formData.style = '卡通'"
                >
                  <div class="style-wrap">
                    <img src="@/assets/static/images/logo/cartoon.png" alt="卡通" />
                    <div class="style-name">卡通</div>
                  </div>
                </div>
              </div>
            </div>
                        
            <!-- 生成数量选择 -->
            <div class="setting-group">
              <h4 class="setting-title">生成数量</h4>
              <div class="count-selection">
                <div 
                  v-for="count in [2, 4]" 
                  :key="count"
                  class="count-option"
                  :class="{ active: formData.generateCount === count }"
                  @click="formData.generateCount = count"
                >
                  {{ count }}张
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 生成按钮 - 置底 -->
        <button 
          class="generate-btn"
          @click="generateLogo"
          :disabled="isGenerating"
        >
         
          <span class="generate-btn-text">
            {{ isGenerating ? '生成中...' : '立即生成' }}
          </span>
          <span v-if="userInfo " class="generate-btn-cost">
            消耗{{ getGenerateCost() }}豆豆
          </span>
          <img v-if="userInfo" src="@/assets/static/images/蒙版组 175.png" alt="豆豆图标" class="generate-btn-icon">
        </button>
        
        <!-- 免责协议 -->
        <div class="disclaimer">
          <a href="#" class="disclaimer-link" @click.prevent="showDisclaimerModal = true">
            <span class="disclaimer-icon">!</span>
            免责声明
          </a>
        </div>
      </div>

      <!-- 右侧展示区域 -->
      <div class="right-panel">
        <div class="logo-showcase">
          <div class="showcase-header">
            <h2 class="showcase-title">
              {{ hasStartedGeneration ? 'AI生成结果' : 'LOGO设计案例' }}
              <span v-if="isLoadingExamples" class="loading-text">（加载中...）</span>
            </h2>
            <div class="header-actions" v-if="generatedLogos.length > 0">
              <button class="clear-btn" @click="clearAllLogos">
                清空所有
              </button>
            </div>
          </div>
          
            <!-- 分类标签 -->
            <div class="category-tabs" v-if="!hasStartedGeneration">
            <button 
              v-for="category in categories" 
              :key="category.id"
              class="category-tab"
              :class="{ active: activeCategory === category.id }"
              @click="activeCategory = category.id"
            >
              {{ category.name }}
            </button>
          </div>

                      <!-- Logo展示网格 -->
          <div class="logo-grid">
            <!-- 正常的Logo展示 -->
            <div 
              v-for="logo in filteredLogos" 
              :key="logo.id"
              class="logo-item"
              @click="handleLogoClick(logo)"
            >
              <div class="logo-image-wrapper" :class="{ 'has-watermark': logo.isGenerated }">
                <img :src="logo.image" :alt="logo.title" />
                <!-- AI生成标签 - 仅对生成的Logo显示 -->
                <div v-if="logo.isGenerated" class="ai-generated-tag">
                  <span class="ai-tag-text">AI生成</span>
                </div>
              </div>
              
              <!-- 工具栏 - 悬停时显示在图片上，仅对生成的Logo显示 -->
              <div v-if="logo.isGenerated" class="logo-toolbar">
                <button 
                  class="toolbar-btn edit-btn" 
                  @click.stop="editLogo(logo)"
                >
                  <Edit />
                  <span>编辑</span>
                </button>
                <button 
                  class="toolbar-btn preview-btn" 
                  @click.stop="previewLogo(logo)"
                >
                  <View />
                  <span>预览</span>
                </button>
                <button 
                  class="toolbar-btn download-btn" 
                  @click.stop="downloadLogo(logo)"
                >
                  <Download />
                  <span>下载</span>
                </button>
              </div>
              
              <!-- 只有非生成结果才显示logo信息 -->
              <div v-if="!logo.isGenerated" class="logo-info-section">
                <h4 class="logo-title" :title="logo.title">
                  {{ logo.isGenerated ? '生成结果' : '灵感' }}：{{ logo.title }}
                </h4>
                <div class="logo-tags">
                  <span v-for="tag in logo.tags" :key="tag" class="logo-tag">
                    {{ tag }}
                  </span>
                </div>
                <!-- 显示AI生成的实际提示词 -->
                <div v-if="logo.isGenerated && logo.actualPrompt" class="logo-prompt">
                  <p class="prompt-text">{{ logo.actualPrompt }}</p>
                </div>
                <!-- 显示处理状态 -->
                <div v-if="logo.isGenerated && (logo.processedUrl || logo.svgUrl || logo.templateSvg)" class="logo-process-info">
                  <div class="process-tags">
                    <span v-if="logo.processedUrl" class="process-tag bg-removed">已去背景</span>
                    <span v-if="logo.svgUrl" class="process-tag svg-converted">SVG格式</span>
                    <span v-if="logo.templateSvg" class="process-tag template-generated">模板生成</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 生成中的加载卡片 - 显示在现有结果后面 -->
            <div 
              v-if="isGenerating"
              v-for="n in remainingLoadingCount" 
              :key="`loading-${n}`"
              class="logo-item loading-item"
            >
              <div class="logo-image-wrapper">
                <div class="loading-placeholder">
                  <div class="loading-spinner"></div>
                  <p class="loading-text">AI生成中...</p>
                  <div class="loading-progress">
                    <div class="progress-bar-mini">
                      <div class="progress-fill-mini" :style="{ width: progress + '%' }"></div>
                    </div>
                    <span class="progress-text-mini">{{ Math.round(progress) }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Close, ArrowLeft, ArrowRight, Check, More, Edit, View, Download } from '@element-plus/icons-vue'
import HeaderNav from './HeaderNav.vue'
import LoginModal from './LoginModal.vue'
import LogoPreviewModal from './LogoPreviewModal.vue'
import VipModal from './VipModal.vue'
import DownloadFormatDialog from './DownloadFormatDialog.vue'
import { generateAILogoApi, queryAILogoTaskApi, buildGenerateText, svgConvertApi, generateTemplateApi, getLogoExamplesApi, getDesignExamplesApi, LogodescApi, visitApi } from '@/api/index.js'

// 响应式数据
const router = useRouter()
const headerNavRef = ref(null) // HeaderNav组件的引用
const showLogin = ref(false)
const isGenerating = ref(false)
const progress = ref(0)
const logoType = ref('graphic')
const selectedModel = ref('model1')
const activeCategory = ref('all')
const generatedLogos = ref([]) // 存储生成的Logo结果
const generatingCount = ref(4) // 正在生成的Logo数量
const hasStartedGeneration = ref(false) // 是否已经开始过生成
const completedCount = ref(0) // 已完成的数量
const designExamples = ref([]) // 存储从API获取的设计师案例
const isLoadingExamples = ref(false) // 是否正在加载案例

// 预览弹窗相关
const showPreviewModal = ref(false)
const previewExamples = ref([])
const previewLoading = ref(false)
const currentPreviewLogo = ref(null)

// 下载格式对话框相关
const showDownloadDialog = ref(false)
const currentDownloadLogo = ref(null)

// 免责声明弹窗相关
const showDisclaimerModal = ref(false)

// VIP弹窗相关
const showVipModal = ref(false)

// 生成描述相关
const isGeneratingDesc = ref(false)

// 用户信息
const userInfo = ref({})
const designSolutions = ref([])
const selectedSolutionIndex = ref(-1)
const showSolutionModal = ref(false)

// 访问追踪相关
const visitStartTime = ref(null)
const stayDuration = ref(0)

// 表单数据
const formData = reactive({
  brandName: '',
  slogan: '',
  description: '',
  style: '简约', // 默认选择简约风格
  industry: 'tech', // 默认选择第一个行业（科技｜IT）
  generateCount: 4 // 新增生成数量
})

// 行业数据 - 静态数据，与API返回的分类保持一致
const industries = ref([
  { id: 'tech', name: '科技｜IT', icon: require('@/assets/static/icon/蒙版组 21.png') },
  { id: 'restaurant', name: '餐饮｜食品', icon: require('@/assets/static/icon/蒙版组 20.png') },
  { id: 'fashion', name: '服装｜饰品', icon: require('@/assets/static/icon/蒙版组 19.png') },
  { id: 'service', name: '生活｜服务', icon: require('@/assets/static/icon/蒙版组 18.png') },
  { id: 'education', name: '教育｜学校', icon: require('@/assets/static/icon/蒙版组 14.png') },
  { id: 'transport', name: '制造｜运输', icon: require('@/assets/static/icon/蒙版组 15.png') },
  { id: 'entertainment', name: '娱乐｜文化', icon: require('@/assets/static/icon/蒙版组 16.png') },
  { id: 'construction', name: '工程｜建筑', icon: require('@/assets/static/icon/蒙版组 17.png') },
  { id: 'health', name: '医疗｜健康', icon: require('@/assets/static/icon/蒙版组 13.png') },
  { id: 'drink', name: '饮品｜酒水', icon: require('@/assets/static/icon/蒙版组 12.png') },
  { id: 'business', name: '商业｜金融', icon: require('@/assets/static/icon/蒙版组 11.png') },
  { id: 'sport', name: '体育｜游戏', icon: require('@/assets/static/icon/蒙版组 10.png') },
  { id: 'pet', name: '宠物｜玩具', icon: require('@/assets/static/icon/蒙版组 6.png') },
  { id: 'beauty', name: '美容｜美业', icon: require('@/assets/static/icon/蒙版组 7.png') },
  { id: 'institution', name: '机构｜政企', icon: require('@/assets/static/icon/蒙版组 8.png') },
  { id: 'baby', name: '母婴｜亲子', icon: require('@/assets/static/icon/蒙版组 9.png') },
  { id: 'exercise', name: '运动｜健身', icon: require('@/assets/static/icon/蒙版组 5.png') },
  { id: 'farm', name: '农业｜生鲜', icon: require('@/assets/static/icon/蒙版组 4.png') },
  { id: 'travel', name: '旅游｜住宿', icon: require('@/assets/static/icon/蒙版组 3.png') },
  { id: 'other', name: '其他', icon: require('@/assets/static/icon/蒙版组 2.png') }
])

// 显示的行业数量和更多弹窗控制
const showIndustryModal = ref(false)
const displayedIndustriesCount = 5

// 生成模型
const models = ref([
  {
    id: 'model1',
    name: '生成模型一',
    description: '矢量，可编辑',
    badge: '推荐',
    type: 'recommended',
    value: 0
  },
  {
    id: 'model2', 
    name: '生成模型二',
    description: '不可编辑，图片效果更佳',
    badge: '即将上线',
    type: 'coming-soon',
    value: 1,
    disabled: true
  }
])

// 分类数据 - 静态数据，包含所有行业分类
const categories = ref([
  { id: 'all', name: '全部', icon: require('@/assets/static/icon/组 23.png') },
  { id: 'tech', name: '科技｜IT', icon: require('@/assets/static/icon/蒙版组 2.png') },
  { id: 'restaurant', name: '餐饮｜食品', icon: require('@/assets/static/icon/蒙版组 3.png') },
  { id: 'fashion', name: '服装｜饰品', icon: require('@/assets/static/icon/蒙版组 4.png') },
  { id: 'service', name: '生活｜服务', icon: require('@/assets/static/icon/蒙版组 5.png') },
  { id: 'education', name: '教育｜学校', icon: require('@/assets/static/icon/蒙版组 6.png') },
  { id: 'transport', name: '制造｜运输', icon: require('@/assets/static/icon/蒙版组 7.png') },
  { id: 'entertainment', name: '娱乐｜文化', icon: require('@/assets/static/icon/蒙版组 8.png') },
  { id: 'construction', name: '工程｜建筑', icon: require('@/assets/static/icon/蒙版组 9.png') },
  { id: 'health', name: '医疗｜健康', icon: require('@/assets/static/icon/蒙版组 10.png') },
  { id: 'drink', name: '饮品｜酒水', icon: require('@/assets/static/icon/蒙版组 11.png') },
  { id: 'business', name: '商业｜金融', icon: require('@/assets/static/icon/蒙版组 12.png') },
  { id: 'sport', name: '体育｜游戏', icon: require('@/assets/static/icon/蒙版组 13.png') },
  { id: 'pet', name: '宠物｜玩具', icon: require('@/assets/static/icon/蒙版组 14.png') },
  { id: 'beauty', name: '美容｜美业', icon: require('@/assets/static/icon/蒙版组 15.png') },
  { id: 'institution', name: '机构｜政企', icon: require('@/assets/static/icon/蒙版组 16.png') },
  { id: 'baby', name: '母婴｜亲子', icon: require('@/assets/static/icon/蒙版组 17.png') },
  { id: 'exercise', name: '运动｜健身', icon: require('@/assets/static/icon/蒙版组 18.png') },
  { id: 'farm', name: '农业｜生鲜', icon: require('@/assets/static/icon/蒙版组 19.png') },
  { id: 'travel', name: '旅游｜住宿', icon: require('@/assets/static/icon/蒙版组 20.png') },
  { id: 'other', name: '其他', icon: require('@/assets/static/icon/蒙版组 21.png') }
])

// API返回的行业分类映射
const industryMapping = {
  '科技｜IT': 'tech',
  '餐饮｜食品': 'restaurant', 
  '服装｜饰品': 'fashion',
  '生活｜服务': 'service',
  '教育｜学校': 'education',
  '制造｜运输': 'transport',
  '娱乐｜文化': 'entertainment',
  '工程｜建筑': 'construction',
  '医疗｜健康': 'health',
  '饮品｜酒水': 'drink',
  '商业｜金融': 'business',
  '体育｜游戏': 'sport',
  '宠物｜玩具': 'pet',
  '美容｜美业': 'beauty',
  '机构｜政企': 'institution',
  '母婴｜亲子': 'baby',
  '运动｜健身': 'exercise',
  '农业｜生鲜': 'farm',
  '旅游｜住宿': 'travel',
  '其他': 'other'
}

// Logo案例数据
const logoExamples = ref([
 
])

// 计算属性
const filteredLogos = computed(() => {
  // 如果已经开始过生成，只显示生成的Logo，不显示案例
  if (hasStartedGeneration.value) {
    return generatedLogos.value
  }
  
  // 合并本地案例和API案例
  const allExamples = [...logoExamples.value, ...designExamples.value]
  
  // 只有在没有开始生成时才显示示例Logo
  if (activeCategory.value === 'all') {
    return allExamples
  }
  return allExamples.filter(logo => logo.category === activeCategory.value)
})

// 计算剩余加载卡片数量
const remainingLoadingCount = computed(() => {
  return Math.max(0, generatingCount.value - completedCount.value)
})

// 方法
const toggleIndustry = (industryId) => {
  // 单选模式：直接设置选中的行业
  formData.industry = industryId
}

// 计算生成消耗的豆豆数量
const getGenerateCost = () => {
  return formData.generateCount === 2 ? 15 : 30
}

const generateLogo = async () => {
  // 验证必填字段
  if (!formData.brandName.trim()) {
    ElMessage.warning('请输入LOGO名称')
    return
  }

  // 验证字数限制
  if (formData.brandName.length > 15) {
    ElMessage.warning('LOGO名称不能超过15个字符')
    return
  }

  if (formData.slogan.length > 20) {
    ElMessage.warning('副标题/口号不能超过20个字符')
    return
  }

  if (formData.description.length > 100) {
    ElMessage.warning('LOGO描述不能超过100个字符')
    return
  }

  // 检查登录状态
  const isLoggedIn = await headerNavRef.value?.checkLoginStatus()
  if (!isLoggedIn) {
    showLogin.value = true
    ElMessage.warning('请先登录后再生成LOGO')
    return
  }

  // 获取当前选中的模型
  const currentModel = models.value.find(model => model.id === selectedModel.value)
  
  // 检查模型是否可用
  if (currentModel && currentModel.disabled) {
    ElMessage.warning('该模型暂未上线，请选择其他模型')
    return
  }

  try {
    let requestData = {}
    const logoCount = formData.generateCount // 使用表单中的生成数量

    if (currentModel && currentModel.value === 0) {
      // 模型值为0时，分开传值
      const selectedIndustryName = formData.industry ? 
        industries.value.find(ind => ind.id === formData.industry)?.name || '' : ''
      
      requestData = {
        brand_name: formData.brandName,
        slogan: formData.slogan || '',
        logo_description: formData.description || '',
        style: formData.style || 'any',
        industry: selectedIndustryName,
        logoType: logoType.value,
        model: currentModel.value,
        n: logoCount // 使用变量而不是固定值
      }
      console.log('分开传值:', requestData)
    } else {
      // 模型值不为0时，组装文本传值
      const selectedIndustryName = formData.industry ? 
        industries.value.find(ind => ind.id === formData.industry)?.name || '' : ''
      const industryNames = selectedIndustryName ? [selectedIndustryName] : []
      
      const generateText = buildGenerateText(formData, industryNames, logoType.value)
      console.log('生成文本:', generateText)
      requestData = {
        text: generateText,
        n: logoCount // 使用变量而不是固定值
      }
    }

    // 调用AI生成接口，获取任务ID
    const response = await generateAILogoApi(requestData)

    console.log('API响应:', response)

    if (response.data.success) {
      // 只有在接口调用成功后才切换到生成状态
      hasStartedGeneration.value = true
      isGenerating.value = true
      progress.value = 0
      completedCount.value = 0 // 重置已完成数量
      
      // 设置加载卡片数量
      generatingCount.value = logoCount
      
      const taskId = response.data.output.task_id
      console.log('获取到任务ID:', taskId)
      
      // 开始轮询查询任务状态
      await pollTaskStatus(taskId)
    } else {
      // 显示API返回的错误信息
      const errorMsg = response.data?.msg || response.data?.message || '提交生成任务失败，请重试'
      ElMessage.error(errorMsg)
      // 不切换到生成状态，保持显示设计案例
    }
  } catch (error) {
    console.error('生成LOGO时发生错误:', error)
    ElMessage.error('生成失败，请检查网络连接后重试')
    // 不切换到生成状态，保持显示设计案例
  }
}

// 轮询查询任务状态
const pollTaskStatus = async (taskId) => {
  const maxAttempts = 60 // 最多查询60次（约5分钟）
  let attempts = 0
  
  const poll = async () => {
    try {
      attempts++
      console.log(`查询任务状态，第${attempts}次尝试`)
      
      // 更新进度条
      progress.value = Math.min(10 + (attempts / maxAttempts) * 80, 90)
      
      const statusResponse = await queryAILogoTaskApi({
        task_id: taskId
      })
      
      console.log('任务状态响应:', statusResponse)
      
      if (statusResponse.data && statusResponse.data.success) {
        const taskStatus = statusResponse.data.output?.task_status
        
        if (taskStatus === 'SUCCEEDED' || taskStatus === 'succeed') {
          // 任务完成
          progress.value = 100
          
          // 处理生成结果
          const results = statusResponse.data.output?.results || []
          
          if (results && results.length > 0) {
            // 检查是否是模型1，需要进行图片后处理
            const currentModel = models.value.find(model => model.id === selectedModel.value)
            
            if (currentModel && currentModel.value === 0) {
              // 模型1需要进行图片后处理
              ElMessage.info('正在进行图片后处理...')
              await processImagesForModel1(results)
            } else {
              // 其他模型直接处理结果
              processGeneratedResults(results)
            }
          } else {
            ElMessage.error('生成结果为空，请重试')
          }
          
          // 完成后重置状态并刷新用户信息
          setTimeout(async () => {
            isGenerating.value = false
            progress.value = 0
            
            // 重新请求用户信息接口，刷新豆豆数量
            if (headerNavRef.value && headerNavRef.value.getUserInfo) {
              console.log('🔄 生成完成后刷新用户信息...')
              await headerNavRef.value.getUserInfo()
              userInfo.value = headerNavRef.value.userInfo || {}
              console.log('✅ 用户信息已刷新:', userInfo.value)
            }
          }, 1000)
          
        } else if (taskStatus === 'FAILED' || taskStatus === 'ERROR') {
          // 任务失败，显示API返回的错误信息
          const errorMsg = statusResponse.data?.msg || statusResponse.data?.message || 'LOGO生成失败，请重试'
          ElMessage.error(errorMsg)
          isGenerating.value = false
          progress.value = 0
          
        } else if (taskStatus === 'PENDING' || taskStatus === 'RUNNING') {
          // 任务进行中，继续轮询
          if (attempts < maxAttempts) {
            setTimeout(poll, 3000) // 3秒后再次查询
          } else {
            ElMessage.error('生成超时，请重试')
            isGenerating.value = false
            progress.value = 0
          }
        } else {
          // 未知状态
          console.log('未知任务状态:', taskStatus)
          if (attempts < maxAttempts) {
            setTimeout(poll, 3000)
          } else {
            ElMessage.error('生成超时，请重试')
            isGenerating.value = false
            progress.value = 0
          }
        }
      } else {
        // 查询失败，显示API返回的错误信息
        const errorMsg = statusResponse.data?.msg || statusResponse.data?.message || '查询任务状态失败'
        if (attempts < maxAttempts) {
          console.warn(`查询失败，重试中... 错误信息: ${errorMsg}`)
          setTimeout(poll, 3000)
        } else {
          ElMessage.error(errorMsg)
          isGenerating.value = false
          progress.value = 0
        }
      }
    } catch (error) {
      console.error('查询任务状态时发生错误:', error)
      if (attempts < maxAttempts) {
        setTimeout(poll, 3000)
      } else {
        ElMessage.error('查询任务状态失败')
        isGenerating.value = false
        progress.value = 0
      }
    }
  }
  
  // 开始轮询
  setTimeout(poll, 2000) // 2秒后开始第一次查询
}

// 处理模型1的图片后处理
const processImagesForModel1 = async (results) => {
  try {
    // 不先显示原图，等处理完毕后再显示
    const processedLogos = []
    
    for (let i = 0; i < results.length; i++) {
      const result = results[i]
      
      try {
        // 第一步：图片处理和SVG转换（合并）
        console.log(`处理第${i + 1}张图片：图片处理和SVG转换`)
        const svgConvertResponse = await svgConvertApi({
          url: result.url,
          goods_id: result.goods_id
        })
        
        if (svgConvertResponse.data && svgConvertResponse.data.success) {
          console.log('SVG转换响应:', svgConvertResponse.data)
          
          // 获取SVG数据
          let svgData = svgConvertResponse.data.optimizedSVG || svgConvertResponse.optimizedSVG
          
          if (svgData) {
            // 如果SVG数据是base64编码的，需要先解码
            if (svgData && svgData.startsWith('data:image/svg+xml;base64,')) {
              try {
                // 提取base64部分并解码
                const base64Part = svgData.split(',')[1]
                svgData = atob(base64Part)
                console.log('SVG解码后:', svgData.substring(0, 100) + '...')
              } catch (error) {
                console.error('SVG解码失败:', error)
                // 如果解码失败，尝试直接使用原数据
              }
            }
            
            // 第二步：生成模板
            console.log(`处理第${i + 1}张图片：生成模板`)
            
            // 从SVG中提取颜色并生成智能配色方案
            const extractedColors = extractColorsFromSvg(svgData)
            const colorScheme = generateColorScheme(extractedColors)
            
            console.log('智能配色方案:', colorScheme)
            
            const templateResponse = await generateTemplateApi({
              brand_name: formData.brandName,
              slogan: formData.slogan || '',
              svg: svgData,
              bg_color: colorScheme.bg_color,
              brand_color: colorScheme.brand_color,
              slogan_color: colorScheme.slogan_color,
              tpl_id: result.tpl_id,
              goods_id: result.goods_id
            })
            
            if (templateResponse.data && templateResponse.data.success) {
              // 模板生成成功，创建最终的Logo对象
              const templateSvg = templateResponse.data.svg || templateResponse.data.output?.svg
              
              // 正确处理包含中文字符的SVG编码
              let svgDataUrl
              try {
                // 使用 encodeURIComponent 来处理包含中文的SVG
                svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(templateSvg)}`
              } catch (error) {
                console.error('SVG编码失败:', error)
                // 如果编码失败，直接使用SVG字符串
                svgDataUrl = `data:image/svg+xml;charset=utf-8,${templateSvg}`
              }
              
              // 创建处理完成的Logo对象
              const processedLogo = {
                id: `generated_${Date.now()}_${i}`,
                title: formData.brandName,
                image: svgDataUrl, // 使用处理后的模板SVG
                tags: [logoType.value === 'graphic' ? '图形LOGO' : '文字LOGO', formData.style || '任意风格'],
                category: 'generated',
                isGenerated: true,
                originalPrompt: result.orig_prompt,
                actualPrompt: result.actual_prompt,
                processedUrl: result.url, // 原始图片URL
                svgData: svgData, // SVG数据
                templateSvg: templateSvg, // 模板SVG
                goods_id: result.goods_id, // 添加goods_id
                tpl_id: result.tpl_id // 添加tpl_id
              }
              
              processedLogos.push(processedLogo)
              
              // 立即显示这个处理完成的Logo
              generatedLogos.value = [...generatedLogos.value, processedLogo]
              
              // 增加已完成数量
              completedCount.value++
              
              console.log(`第${i + 1}张图片处理完成并已显示`)
            } else {
              // 模板生成失败，使用SVG结果
              const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`
              const processedLogo = {
                id: `generated_${Date.now()}_${i}`,
                title: formData.brandName,
                image: svgDataUrl, // 使用SVG
                tags: [logoType.value === 'graphic' ? '图形LOGO' : '文字LOGO', formData.style || '任意风格'],
                category: 'generated',
                isGenerated: true,
                originalPrompt: result.orig_prompt,
                actualPrompt: result.actual_prompt,
                processedUrl: result.url,
                svgData: svgData,
                goods_id: result.goods_id, // 添加goods_id
                tpl_id: result.tpl_id // 添加tpl_id
              }
              
              processedLogos.push(processedLogo)
              generatedLogos.value = [...generatedLogos.value, processedLogo]
              
              // 增加已完成数量
              completedCount.value++
            }
          }
        } else {
          // SVG转换失败，使用原图
          const fallbackLogo = {
            id: `generated_${Date.now()}_${i}`,
            title: formData.brandName,
            image: result.url, // 使用原图
            tags: [logoType.value === 'graphic' ? '图形LOGO' : '文字LOGO', formData.style || '任意风格'],
            category: 'generated',
            isGenerated: true,
            originalPrompt: result.orig_prompt,
            actualPrompt: result.actual_prompt,
            goods_id: result.goods_id, // 添加goods_id
            tpl_id: result.tpl_id // 添加tpl_id
          }
          
          processedLogos.push(fallbackLogo)
          generatedLogos.value = [...generatedLogos.value, fallbackLogo]
          
          // 增加已完成数量
          completedCount.value++
        }
      } catch (error) {
        console.error(`处理第${i + 1}张图片时发生错误:`, error)
        // 处理失败时使用原图
        const fallbackLogo = {
          id: `generated_${Date.now()}_${i}`,
          title: formData.brandName,
          image: result.url,
          tags: [logoType.value === 'graphic' ? '图形LOGO' : '文字LOGO', formData.style || '任意风格'],
          category: 'generated',
          isGenerated: true,
          originalPrompt: result.orig_prompt,
          actualPrompt: result.actual_prompt,
          goods_id: result.goods_id, // 添加goods_id
          tpl_id: result.tpl_id // 添加tpl_id
        }
        
        processedLogos.push(fallbackLogo)
        generatedLogos.value = [...generatedLogos.value, fallbackLogo]
        
        // 增加已完成数量
        completedCount.value++
      }
    }
    
    ElMessage.success('所有图片后处理完成！')
    
    // 图片后处理完成后也刷新用户信息
    if (headerNavRef.value && headerNavRef.value.getUserInfo) {
      console.log('🔄 图片后处理完成后刷新用户信息...')
      await headerNavRef.value.getUserInfo()
      userInfo.value = headerNavRef.value.userInfo || {}
      console.log('✅ 用户信息已刷新:', userInfo.value)
    }
    
  } catch (error) {
    console.error('图片后处理失败:', error)
    ElMessage.warning('图片后处理失败')
  }
}

// 将图片URL转换为base64
const convertImageToBase64 = async (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    
    img.onload = function() {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      canvas.width = img.width
      canvas.height = img.height
      
      ctx.drawImage(img, 0, 0)
      
      const base64 = canvas.toDataURL('image/png').split(',')[1]
      resolve(base64)
    }
    
    img.onerror = function() {
      reject(new Error('Failed to load image'))
    }
    
    img.src = imageUrl
  })
}

// SVG颜色提取算法
const extractColorsFromSvg = (svgString) => {
  try {
    // 创建一个临时的DOM元素来解析SVG
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgString, 'image/svg+xml')
    
    const colors = new Set()
    const elements = svgDoc.querySelectorAll('*')
    
    // 提取所有颜色属性
    elements.forEach(element => {
      // 检查fill属性
      const fill = element.getAttribute('fill')
      if (fill && fill !== 'none' && fill !== 'transparent') {
        colors.add(fill)
      }
      
      // 检查stroke属性
      const stroke = element.getAttribute('stroke')
      if (stroke && stroke !== 'none' && stroke !== 'transparent') {
        colors.add(stroke)
      }
      
      // 检查style属性中的颜色
      const style = element.getAttribute('style')
      if (style) {
        const fillMatch = style.match(/fill:\s*([^;]+)/)
        const strokeMatch = style.match(/stroke:\s*([^;]+)/)
        
        if (fillMatch && fillMatch[1] !== 'none' && fillMatch[1] !== 'transparent') {
          colors.add(fillMatch[1].trim())
        }
        if (strokeMatch && strokeMatch[1] !== 'none' && strokeMatch[1] !== 'transparent') {
          colors.add(strokeMatch[1].trim())
        }
      }
    })
    
    // 过滤并转换颜色格式
    const validColors = Array.from(colors).filter(color => {
      return color && 
             color !== 'currentColor' && 
             color !== 'inherit' &&
             !color.includes('url(') // 排除渐变等复杂填充
    }).map(color => normalizeColor(color))
    
    console.log('提取的颜色:', validColors)
    return validColors
    
  } catch (error) {
    console.error('SVG颜色提取失败:', error)
    return []
  }
}

// 颜色格式标准化
const normalizeColor = (color) => {
  // 如果已经是hex格式，直接返回
  if (color.startsWith('#')) {
    return color.length === 4 ? 
      '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3] : 
      color
  }
  
  // 处理rgb格式
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1])
    const g = parseInt(rgbMatch[2])
    const b = parseInt(rgbMatch[3])
    return rgbToHex(r, g, b)
  }
  
  // 处理颜色名称
  const colorNames = {
    'black': '#000000',
    'white': '#ffffff',
    'red': '#ff0000',
    'green': '#008000',
    'blue': '#0000ff',
    'yellow': '#ffff00',
    'cyan': '#00ffff',
    'magenta': '#ff00ff',
    'gray': '#808080',
    'grey': '#808080'
  }
  
  return colorNames[color.toLowerCase()] || color
}

// RGB转HEX
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')
}

// 颜色亮度计算
const getColorBrightness = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return (r * 299 + g * 587 + b * 114) / 1000
}

// 智能配色方案生成
const generateColorScheme = (extractedColors) => {
  if (extractedColors.length === 0) {
    // 如果没有提取到颜色，使用默认配色
    return {
      bg_color: '#ffffff',
      brand_color: '#000000',
      slogan_color: '#666666'
    }
  }
  
  // 排除纯白和纯黑，优先选择有色彩的颜色
  const coloredColors = extractedColors.filter(color => 
    color !== '#ffffff' && 
    color !== '#000000' && 
    color !== '#fff' && 
    color !== '#000'
  )
  
  let brandColor = coloredColors.length > 0 ? coloredColors[0] : extractedColors[0]
  
  // 如果品牌色太亮，选择较深的颜色
  if (getColorBrightness(brandColor) > 200) {
    const darkerColors = extractedColors.filter(color => getColorBrightness(color) < 150)
    if (darkerColors.length > 0) {
      brandColor = darkerColors[0]
    }
  }
  
  // 生成背景色 - 通常选择浅色
  let bgColor = '#ffffff'
  const lightColors = extractedColors.filter(color => getColorBrightness(color) > 240)
  if (lightColors.length > 0 && lightColors[0] !== brandColor) {
    bgColor = lightColors[0]
  }
  
  // 生成标语色 - 通常比品牌色浅一些
  let sloganColor = '#666666'
  const brandBrightness = getColorBrightness(brandColor)
  
  if (brandBrightness < 100) {
    // 品牌色很深，标语用中等亮度
    sloganColor = '#666666'
  } else if (brandBrightness > 150) {
    // 品牌色较亮，标语用较深色
    sloganColor = '#333333'
  } else {
    // 品牌色中等，标语用稍浅的同色系
    sloganColor = lightenColor(brandColor, 0.3)
  }
  
  return {
    bg_color: bgColor,
    brand_color: brandColor,
    slogan_color: sloganColor
  }
}

// 颜色变亮函数
const lightenColor = (hex, factor) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  
  const newR = Math.min(255, Math.round(r + (255 - r) * factor))
  const newG = Math.min(255, Math.round(g + (255 - g) * factor))
  const newB = Math.min(255, Math.round(b + (255 - b) * factor))
  
  return rgbToHex(newR, newG, newB)
}

// 通用结果处理函数
const processGeneratedResults = (results) => {
  const newLogos = results.map((result, index) => ({
    id: `generated_${Date.now()}_${index}`,
    title: formData.brandName,
    image: result.url,
    tags: [logoType.value === 'graphic' ? '图形LOGO' : '文字LOGO', formData.style || '任意风格'],
    category: 'generated',
    isGenerated: true,
    originalPrompt: result.orig_prompt,
    actualPrompt: result.actual_prompt,
    processedUrl: result.processedUrl, // 处理后的URL（如果有）
    svgUrl: result.svgUrl, // SVG URL（如果有）
    templateSvg: result.templateSvg, // 模板SVG（如果有）
    goods_id: result.goods_id, // 添加goods_id
    tpl_id: result.tpl_id // 添加tpl_id
  }))

  // 将新生成的Logo添加到现有Logo后面
  generatedLogos.value = [...generatedLogos.value, ...newLogos]
  
  // 更新已完成数量
  completedCount.value = generatingCount.value

  // 切换到显示生成结果
  activeCategory.value = 'all'
  
  ElMessage.success(`成功生成${newLogos.length}个LOGO设计！`)
}


// 关闭预览模态框
const closePreviewModal = () => {
  showPreviewModal.value = false
  currentPreviewLogo.value = null
  previewExamples.value = []
}

// 处理Logo点击事件
const handleLogoClick = (logo) => {
  if (!logo.isGenerated) {
    // 如果是案例（非生成的Logo）且有actualPrompt，将其置入文字描述字段
    if (logo.title) {
      formData.description = logo.title
      console.log('已将案例的actualPrompt置入文字描述:', logo.title)
      ElMessage.success('已应用案例描述到文字描述字段')
    } else {
      ElMessage.info('该案例暂无描述信息')
    }
  } else {
    // 如果是生成的Logo，打开预览
    previewLogo(logo)
  }
}

const previewLogo = async (logo) => {
  console.log('预览LOGO:', logo)
  
  // 准备传入预览弹窗的Logo数据，确保包含SVG数据
  let logoForPreview = { ...logo }
  
  // 优先使用templateSvg，这是完整的模板SVG
  if (logoForPreview.templateSvg) {
    logoForPreview.svgData = logoForPreview.templateSvg
    console.log('✅ 使用templateSvg作为预览数据')
  } else if (logoForPreview.svgData) {
    console.log('✅ 使用svgData作为预览数据')
  } else if (logoForPreview.image) {
    // 检查image的类型
    if (typeof logoForPreview.image === 'string') {
      if (logoForPreview.image.startsWith('data:image/svg+xml')) {
        // 从data URL中提取SVG内容
        try {
          const base64Match = logoForPreview.image.match(/data:image\/svg\+xml;charset=utf-8,(.+)/)
          if (base64Match) {
            logoForPreview.svgData = decodeURIComponent(base64Match[1])
            console.log('✅ 从data URL提取SVG数据成功')
          }
        } catch (error) {
          console.error('从data URL提取SVG数据失败:', error)
        }
      } else if (logoForPreview.image.startsWith('http')) {
        // 如果是URL，尝试获取SVG内容
        try {
          const response = await fetch(logoForPreview.image)
          const svgText = await response.text()
          if (svgText.includes('<svg')) {
            logoForPreview.svgData = svgText
            console.log('✅ 从URL获取SVG数据成功')
          }
        } catch (error) {
          console.error('从URL获取SVG数据失败:', error)
        }
      }
    } else {
      console.warn('logo.image 不是字符串类型:', typeof logoForPreview.image, logoForPreview.image)
    }
  }
  
  console.log('传入预览弹窗的Logo数据:', {
    id: logoForPreview.id,
    title: logoForPreview.title,
    hasImage: !!logoForPreview.image,
    imageType: typeof logoForPreview.image,
    imageValue: logoForPreview.image,
    hasSvgData: !!logoForPreview.svgData,
    svgDataLength: logoForPreview.svgData ? logoForPreview.svgData.length : 0,
    svgDataPreview: logoForPreview.svgData ? logoForPreview.svgData.substring(0, 100) + '...' : null,
    hasTemplateSvg: !!logoForPreview.templateSvg,
    templateSvgLength: logoForPreview.templateSvg ? logoForPreview.templateSvg.length : 0,
    templateSvgPreview: logoForPreview.templateSvg ? logoForPreview.templateSvg.substring(0, 100) + '...' : null,
    isGenerated: logoForPreview.isGenerated,
    goods_id: logoForPreview.goods_id
  })
  
  // 设置当前预览的Logo并显示弹窗
  currentPreviewLogo.value = logoForPreview
  showPreviewModal.value = true
  previewExamples.value = []
  
  // 对于非生成的Logo，直接显示
  if (!logo.isGenerated) {
    previewLoading.value = false
  }
}


const handleNavClick = (nav) => {
  console.log('导航点击:', nav)
}

const handleLoginSuccess = async () => {
  console.log('🎉 handleLoginSuccess 被调用了！')
  showLogin.value = false
  ElMessage.success('登录成功！')
  
  // 立即刷新状态，减少延迟
  if (headerNavRef.value && headerNavRef.value.checkLoginStatus) {
    console.log('立即刷新HeaderNav状态...')
    try {
      const loginStatus = await headerNavRef.value.checkLoginStatus()
      console.log('checkLoginStatus返回结果:', loginStatus)
      userInfo.value = headerNavRef.value.userInfo || {}
      console.log('登录成功后刷新用户状态:', userInfo.value)
    } catch (error) {
      console.error('刷新状态失败:', error)
      // 如果立即刷新失败，延迟100ms重试一次
      setTimeout(async () => {
        if (headerNavRef.value && headerNavRef.value.checkLoginStatus) {
          await headerNavRef.value.checkLoginStatus()
          userInfo.value = headerNavRef.value.userInfo || {}
        }
      }, 100)
    }
  }
}

const handleLoginModalUpdate = (visible) => {
  console.log('📱 LoginModal visible 状态变化:', visible)
  showLogin.value = visible
}

const handleLogout = () => {
  ElMessage.success('退出登录成功！')
}

const handleHistory = () => {
  router.push('/history')
}

// VIP相关事件处理
const handleVipConfirm = () => {
  showVipModal.value = false
  ElMessage.success('VIP开通成功！')
}

const handleUpdateUserInfo = async () => {
  // 通过HeaderNav组件更新用户信息
  if (headerNavRef.value && headerNavRef.value.getUserInfo) {
    await headerNavRef.value.getUserInfo()
    userInfo.value = headerNavRef.value.userInfo || {}
  }
}

// VIP下载处理
const handleVipDownload = () => {
  ElMessage.info('VIP专属下载功能开发中...')
}

// 预览组件的下载事件处理
const handlePreviewDownload = (data) => {
  const { logo, exampleIndex, previewImage, originalSvg, isOriginalSvg } = data
  console.log('预览下载:', { logo, exampleIndex, previewImage, originalSvg, isOriginalSvg })
  
  // 根据当前选择下载相应内容
  if (isOriginalSvg && originalSvg) {
    // 下载原始SVG
    const tempLogo = {
      ...logo,
      svgData: originalSvg,
      title: `${logo.title}_原始SVG`
    }
    downloadLogo(tempLogo)
  } else if (previewImage) {
    // 下载预览图（这里可能需要特殊处理，因为是图片URL）
    const tempLogo = {
      ...logo,
      image: previewImage,
      title: `${logo.title}_样式${exampleIndex}`
    }
    downloadLogo(tempLogo)
  } else {
    downloadLogo(logo)
  }
}

// 预览组件的VIP下载事件处理
const handlePreviewVipDownload = (data) => {
  const { logo, exampleIndex, previewImage, originalSvg, isOriginalSvg } = data
  console.log('预览VIP下载:', { logo, exampleIndex, previewImage, originalSvg, isOriginalSvg })
  ElMessage.info('VIP专属下载功能开发中...')
}

// 生成图标描述
const generateLogoDescription = async () => {
  // 检查必填项
  if (!formData.brandName) {
    ElMessage.warning('请先填写LOGO名称')
    return
  }
  
  if (!formData.industry) {
    ElMessage.warning('请先选择行业')
    return
  }
  
  try {
    isGeneratingDesc.value = true
    
    // 获取行业名称
    const selectedIndustry = industries.value.find(ind => ind.id === formData.industry)
    const industryName = selectedIndustry ? selectedIndustry.name : ''
    
    // 调用接口
    const response = await LogodescApi({
      brand_name: formData.brandName,
      industry: industryName
    })
    
    console.log('生成描述API响应:', response)
    
    // 检查响应数据，这个API的响应格式是直接返回数据，没有success字段
    // 兼容两种响应格式：1. 直接返回数据 2. 有success字段的格式
    const responseData = response.data
    // 如果响应有success字段，使用responseData；否则直接使用responseData（数据就在responseData中）
    const data =  responseData
    
    if (!data) {
      ElMessage.error('生成描述失败，请稍后重试')
      return
    }
    console.log(data);
    
    // 使用 design_recommendations 填充 description
    let descriptionText = ''
    

    // 如果没有 design_recommendations，使用 design_suggestions（兼容旧格式）
     if (data.design_solutions && Array.isArray(data.design_solutions) && data.design_solutions.length > 0) {
        descriptionText = data.design_solutions[0].description

        console.log(data.design_solutions[0].description);
    }
    
    if (descriptionText) {
      // 如果描述文本超过100字符，截取前100字符
      if (descriptionText.length > 100) {
        descriptionText = descriptionText.substring(0, 100)
      }
      
      // 填充到描述框
      formData.description = descriptionText
      
      ElMessage.success('描述生成成功！')
    } else {
      ElMessage.error(responseData?.message || '生成描述失败，未找到有效的描述数据')
    }
    
    // 如果有 design_solutions（设计方案），显示方案选择弹窗
    if (data.design_solutions && Array.isArray(data.design_solutions) && data.design_solutions.length > 0) {
      designSolutions.value = data.design_solutions
      selectedSolutionIndex.value = -1
      showSolutionModal.value = true
    }
  } catch (error) {
    console.error('生成描述失败:', error)
    ElMessage.error('生成描述失败，请稍后重试')
  } finally {
    isGeneratingDesc.value = false
  }
}

// 选择设计方案
const selectSolution = (index) => {
  selectedSolutionIndex.value = index
}

// 确认选择设计方案
const confirmSolution = () => {
  if (selectedSolutionIndex.value >= 0 && selectedSolutionIndex.value < designSolutions.value.length) {
    const selectedSolution = designSolutions.value[selectedSolutionIndex.value]
    console.log('用户选择的设计方案:', selectedSolution)
    
    // 将选中方案的描述赋值给图标描述字段
    if (selectedSolution.description) {
      // 如果描述超过100字符，截取前100字符
      const descriptionText = selectedSolution.description.length > 100 
        ? selectedSolution.description.substring(0, 100) 
        : selectedSolution.description
      formData.description = descriptionText
    }
    
    ElMessage.success(`已选择方案：${selectedSolution.name}`)
    
    showSolutionModal.value = false
  } else {
    ElMessage.warning('请先选择一个设计方案')
  }
}

// 加载设计师案例
const loadDesignExamples = async (page = 1, limit = 20) => {
  try {
    isLoadingExamples.value = true
    
    const response = await getDesignExamplesApi({
      page: page.toString(),
      limit: limit.toString()
    })
    
    console.log('设计师案例API响应:', response)
    
    if (response.data && response.data.success && response.data.data) {
      const apiExamples = response.data.data
        .filter(item => item.image_url) // 只显示有图片的案例
        .map(item => ({
          id: `api_${item.id}`,
          title: item.title,
          image: item.image_url,
          tags: [item.style_type + 'LOGO', item.style_feature || '设计师案例'],
          category: industryMapping[item.industry] || 'other',
          industry: item.industry,
          styleType: item.style_type,
          styleFeature: item.style_feature,
          isApiExample: true // 标记为API案例
        }))
      
      designExamples.value = apiExamples
      console.log('转换后的设计师案例:', apiExamples)
      
    } else {
      console.warn('设计师案例API返回数据格式异常:', response.data)
    }
  } catch (error) {
    console.error('加载设计师案例失败:', error)
    ElMessage.warning('加载设计师案例失败，将显示默认案例')
  } finally {
    isLoadingExamples.value = false
  }
}

// 编辑Logo功能
const editLogo = (logo) => {
  console.log('编辑LOGO:', logo)
  
  // 准备传递给编辑器的SVG数据
  let svgData = ''
  
  if (logo.templateSvg) {
    // 优先使用模板SVG
    svgData = logo.templateSvg
  } else if (logo.svgData) {
    // 其次使用SVG数据
    svgData = logo.svgData
  } else if (logo.image && logo.image.startsWith('data:image/svg+xml')) {
    // 从data URL中提取SVG内容
    try {
      const base64Match = logo.image.match(/data:image\/svg\+xml;charset=utf-8,(.+)/)
      if (base64Match) {
        svgData = decodeURIComponent(base64Match[1])
      }
    } catch (error) {
      console.error('SVG解码失败:', error)
    }
  }
  
  if (!svgData) {
    ElMessage.error('该Logo暂无可编辑的SVG数据')
    return
  }
  
  // 将SVG数据编码后传递到编辑器页面
  const encodedSvgData = encodeURIComponent(svgData)
  const logoInfo = {
    title: logo.title,
    brandName: formData.brandName,
    slogan: formData.slogan,
    industry: formData.industry,
    style: formData.style,
    logoType: logoType.value
  }
  
  // 将数据存储到sessionStorage中，供编辑器页面使用
  sessionStorage.setItem('editLogoData', JSON.stringify({
    svgData: encodedSvgData,
    logoInfo: logoInfo,
    originalLogo: logo
  }))
  
  // 跳转到编辑器页面
  router.push('/canvas-editor')
  ElMessage.success('正在进入编辑器...')
}

// 下载Logo功能
const downloadLogo = async (logo) => {
  console.log('下载LOGO:', logo)
  
  try {
    // 检查用户是否已登录
    if (!userInfo.value || !userInfo.value.id) {
      ElMessage.warning('请先登录后再下载')
      showLogin.value = true
      return
    }
    
    // 判断是否为VIP会员
    const isVip = userInfo.value.member_level && parseInt(userInfo.value.member_level) > 1
    
    if (!isVip) {
      // 不是VIP会员，显示VIP弹窗
      ElMessage.warning('下载功能需要VIP会员权限')
      showVipModal.value = true
      return
    }
    
    ElMessage.info('正在准备下载...')
    
    // 设置当前要下载的Logo并显示格式选择对话框
    currentDownloadLogo.value = logo
    showDownloadDialog.value = true
    
  } catch (error) {
    console.error('下载失败:', error)
    ElMessage.error('下载失败，请稍后重试')
  }
}

// 处理下载格式选择 - 现在由DownloadFormatDialog组件内部处理
const handleDownloadFormatConfirm = async (format) => {
  // 下载逻辑已经移到DownloadFormatDialog组件中
  // 这里只需要关闭对话框
  showDownloadDialog.value = false
  currentDownloadLogo.value = null
}


// 关闭下载对话框
const handleDownloadDialogClose = () => {
  showDownloadDialog.value = false
  currentDownloadLogo.value = null
}


// 将SVG转换为图片并下载
const downloadSvgAsImage = (svgContent, filename, format, customWidth = null, customHeight = null) => {
  return new Promise((resolve, reject) => {
    try {
      // 创建一个临时的SVG元素来获取尺寸
      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')
      const svgElement = svgDoc.documentElement
      
      // 获取SVG的原始尺寸
      let width = 800 // 默认宽度
      let height = 600 // 默认高度
      
      // 如果指定了自定义尺寸，使用自定义尺寸
      if (customWidth && customHeight) {
        width = customWidth
        height = customHeight
        
        // 简单直接的方法：修改SVG尺寸和viewBox
        svgElement.setAttribute('width', width)
        svgElement.setAttribute('height', height)

        
        // 调整背景矩形
        const backgroundRect = svgElement.querySelector('rect[id="bg"]')
        if (backgroundRect) {
          backgroundRect.setAttribute('width', "2000%")
          backgroundRect.setAttribute('height', "2000%")
          backgroundRect.setAttribute('x', '-1000%')
          backgroundRect.setAttribute('y', '-1000%')
        }
        
        // 重新序列化SVG内容
        const serializer = new XMLSerializer()
        svgContent = serializer.serializeToString(svgElement)
      } else {
        // 否则从SVG中获取尺寸
        const viewBox = svgElement.getAttribute('viewBox')
        if (viewBox) {
          const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number)
          if (vbWidth && vbHeight) {
            width = vbWidth
            height = vbHeight
          }
        } else {
          const svgWidth = svgElement.getAttribute('width')
          const svgHeight = svgElement.getAttribute('height')
          if (svgWidth && svgHeight) {
            width = parseInt(svgWidth)
            height = parseInt(svgHeight)
          }
        }
      }
      
      // 设置高分辨率（自定义尺寸时使用1倍分辨率，其他情况使用2倍）
      const scale = (customWidth && customHeight) ? 1 : 2
      const canvasWidth = width * scale
      const canvasHeight = height * scale
      
      
      // 创建canvas
      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      const ctx = canvas.getContext('2d')
      
      // 设置高质量渲染
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      
      // 如果是JPG格式，先填充白色背景
      if (format === 'jpg') {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvasWidth, canvasHeight)
      }
      
      // 创建图片对象
      const img = new Image()
      img.onload = function() {
        // 绘制图片到canvas
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)
        
        // 转换为blob并下载
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${filename}.${format}`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
            
            ElMessage.success(`${format.toUpperCase()}下载成功`)
            resolve()
          } else {
            reject(new Error('转换失败'))
          }
        }, format === 'jpg' ? 'image/jpeg' : 'image/png', 0.95)
      }
      
      img.onerror = function() {
        reject(new Error('图片加载失败'))
      }
      
      // 将修改后的SVG转换为Data URL
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
      const svgUrl = URL.createObjectURL(svgBlob)
      img.src = svgUrl
      
    } catch (error) {
      reject(error)
    }
  })
}

// 下载文件函数
const downloadFile = async (url, filename) => {
  try {
    ElMessage.info('正在准备下载...')
    
    if (url.startsWith('data:')) {
      // 处理data URL (如SVG)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      ElMessage.success('下载成功！')
    } else {
      // 处理普通URL
      const response = await fetch(url)
      const blob = await response.blob()
      
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      ElMessage.success('下载成功！')
    }
  } catch (error) {
    console.error('下载文件失败:', error)
    ElMessage.error('下载失败，请重试')
    throw error
  }
}

// 重新生成
const regenerateLogo = () => {
  // 不清空现有结果，重新生成会追加到后面
  // generatedLogos.value = [] // 注释掉清空逻辑
  
  // 重新生成，新Logo会追加到现有Logo后面
  generateLogo()
}

// 清空所有LOGO
const clearAllLogos = () => {
  ElMessage.info('正在清空所有LOGO...')
  generatedLogos.value = []
  hasStartedGeneration.value = false // 重置生成状态，重新显示设计案例
  completedCount.value = 0 // 重置已完成数量
  
  // 清除保存的状态
  clearSavedState()
  
  ElMessage.success('所有LOGO已清空！')
}

// 状态持久化相关函数
const saveStateToLocalStorage = () => {
  try {
    const state = {
      generatedLogos: generatedLogos.value,
      hasStartedGeneration: hasStartedGeneration.value,
      completedCount: completedCount.value,
      formData: { ...formData },
      logoType: logoType.value,
      selectedModel: selectedModel.value,
      activeCategory: activeCategory.value,
      timestamp: Date.now()
    }
    localStorage.setItem('aiLogoGeneratorState', JSON.stringify(state))
    console.log('状态已保存到localStorage')
  } catch (error) {
    console.error('保存状态失败:', error)
  }
}

const loadStateFromLocalStorage = () => {
  try {
    const savedState = localStorage.getItem('aiLogoGeneratorState')
    if (savedState) {
      const state = JSON.parse(savedState)
      
      // 检查状态是否过期（24小时）
      const isExpired = Date.now() - state.timestamp > 24 * 60 * 60 * 1000
      if (isExpired) {
        console.log('保存的状态已过期，清除缓存')
        localStorage.removeItem('aiLogoGeneratorState')
        return false
      }
      
      // 恢复状态
      if (state.generatedLogos && state.generatedLogos.length > 0) {
        generatedLogos.value = state.generatedLogos
        hasStartedGeneration.value = state.hasStartedGeneration || false
        completedCount.value = state.completedCount || 0
        
        // 恢复表单数据
        if (state.formData) {
          Object.assign(formData, state.formData)
        }
        
        // 恢复其他状态
        logoType.value = state.logoType || 'graphic'
        selectedModel.value = state.selectedModel || 'model1'
        activeCategory.value = state.activeCategory || 'all'
        
        console.log('状态已从localStorage恢复，生成的Logo数量:', generatedLogos.value.length)
        ElMessage.success(`已恢复${generatedLogos.value.length}个之前生成的Logo`)
        return true
      }
    }
  } catch (error) {
    console.error('恢复状态失败:', error)
    localStorage.removeItem('aiLogoGeneratorState')
  }
  return false
}

const clearSavedState = () => {
  try {
    localStorage.removeItem('aiLogoGeneratorState')
    console.log('已清除保存的状态')
  } catch (error) {
    console.error('清除状态失败:', error)
  }
}

// 访问追踪相关函数
// 生成浏览器指纹
const generateFingerprint = () => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  ctx.textBaseline = 'top'
  ctx.font = '14px "Arial"'
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = '#f60'
  ctx.fillRect(125, 1, 62, 20)
  ctx.fillStyle = '#069'
  ctx.fillText('Browser fingerprint', 2, 15)
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
  ctx.fillText('Browser fingerprint', 4, 17)
  
  const fingerprint = canvas.toDataURL()
  const hash = simpleHash(fingerprint)
  
  return 'sess_' + hash + '_' + Date.now()
}

// 简单哈希函数
const simpleHash = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return Math.abs(hash).toString(36)
}

// 解析URL参数
const getUrlParams = () => {
  const params = {}
  const urlParams = new URLSearchParams(window.location.search)
  
  params.kw = urlParams.get('kw') || ''
  params.kw_enc = urlParams.get('kw_enc') || ''
  params.planid = urlParams.get('planid') || ''
  params.keywordid = urlParams.get('keywordid') || ''
  params.unitid = urlParams.get('unitid') || ''
  params.creativeid = urlParams.get('creativeid') || ''
  params.engine = urlParams.get('engine') || ''
  
  return params
}

// 检测设备类型
const getDeviceType = () => {
  const ua = navigator.userAgent
  if (/Mobile|Android|iPhone|iPad/.test(ua)) {
    return 'mobile'
  }
  return 'pc'
}

// 检测浏览器
const getBrowser = () => {
  const ua = navigator.userAgent
  if (ua.indexOf('Chrome') > -1) return 'Chrome'
  if (ua.indexOf('Firefox') > -1) return 'Firefox'
  if (ua.indexOf('Safari') > -1) return 'Safari'
  if (ua.indexOf('Edge') > -1) return 'Edge'
  if (ua.indexOf('Opera') > -1) return 'Opera'
  return 'Unknown'
}

// 检测操作系统
const getOS = () => {
  const ua = navigator.userAgent
  if (ua.indexOf('Windows') > -1) return 'Windows'
  if (ua.indexOf('Mac') > -1) return 'Mac'
  if (ua.indexOf('Linux') > -1) return 'Linux'
  if (ua.indexOf('Android') > -1) return 'Android'
  if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1) return 'iOS'
  return 'Unknown'
}

// 更新停留时间
const updateDuration = async () => {
  try {
    const sessionId = localStorage.getItem('session_id')
    if (!sessionId) return
    
    const duration = Math.floor((Date.now() - visitStartTime.value) / 1000)
    stayDuration.value = duration
    
    console.log('停留时间已更新:', duration + '秒')
  } catch (error) {
    console.log('更新停留时间失败:', error)
  }
}

// 发送访问追踪
const trackVisit = async (isExit = false) => {
  try {
    const sessionId = localStorage.getItem('session_id') || generateFingerprint()
    localStorage.setItem('session_id', sessionId)
    
    const urlParams = getUrlParams()
    const currentTime = new Date().toISOString()
    
    if (isExit && visitStartTime.value) {
      stayDuration.value = Math.floor((Date.now() - visitStartTime.value) / 1000)
    }
    
    const trackData = {
      session_id: sessionId,
      user_agent: navigator.userAgent,
      kw: urlParams.kw || '',
      kw_enc: urlParams.kw_enc || '',
      planid: urlParams.planid || '',
      keywordid: urlParams.keywordid || '',
      unitid: urlParams.unitid || '',
      creativeid: urlParams.creativeid || '',
      engine: urlParams.engine || 'bd',
      referrer: document.referrer || '',
      landing_page: window.location.href,
      visit_time: currentTime,
      stay_duration: stayDuration.value,
      page_views: 1,
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      custom_params: JSON.stringify({
        page: 'ai-logo-generator',
        has_generated: hasStartedGeneration.value,
        generated_count: generatedLogos.value.length
      })
    }
    
    const res = await visitApi(trackData)
    console.log('访问追踪成功', isExit ? '(退出)' : '(进入)')
  } catch (error) {
    console.log('访问追踪失败:', error)
  }
}

// 监听状态变化，自动保存
watch([generatedLogos, hasStartedGeneration, formData], () => {
  if (generatedLogos.value.length > 0) {
    saveStateToLocalStorage()
  }
}, { deep: true })

onMounted(async () => {
  console.log('AI Logo Generator mounted')
  
  // 初始化访问追踪
  visitStartTime.value = Date.now()
  await trackVisit(false)
  
  // 获取用户信息
  if (headerNavRef.value && headerNavRef.value.getUserInfo) {
    await headerNavRef.value.getUserInfo()
    userInfo.value = headerNavRef.value.userInfo || {}
  }
  
  // 先尝试恢复之前的状态
  const hasRestoredState = loadStateFromLocalStorage()
  
  // 加载设计师案例
  await loadDesignExamples()
  
  // 如果没有恢复状态，显示欢迎信息
  if (!hasRestoredState) {
    console.log('首次访问或无保存状态')
  }
  
  // 添加全局事件监听作为备用方案
  const handleGlobalLoginSuccess = (event) => {
    console.log('🌐 收到全局登录成功事件:', event.detail)
    handleLoginSuccess()
  }
  
  window.addEventListener('login-success', handleGlobalLoginSuccess)
  
  // 清理事件监听器
  onUnmounted(() => {
    window.removeEventListener('login-success', handleGlobalLoginSuccess)
  })
})

onUnmounted(() => {
  // 页面退出时发送访问追踪
  trackVisit(true)
})
</script>

<style scoped>
/* 隐藏整个页面的滚动条 */
:global(html, body) {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

:global(html::-webkit-scrollbar, body::-webkit-scrollbar) {
  display: none;
}

.ai-logo-generator {
  min-height: 100vh;
  background-color: #ffffff;
}

/* 固定头部样式 */
.header-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  
}

.main-container {
  display: flex;
  min-height: calc(100vh - 60px);
  margin: 0 auto;
  padding-top: 60px; /* 为固定头部留出空间 */
}

/* 隐藏整个页面的滚动条 */
.main-container::-webkit-scrollbar {
  display: none;
}

.left-panel {
  width: 350px;
  background: #F2F7F7;
  padding: 24px 24px 0;
  min-height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
  position: sticky;
  top: 60px; /* 考虑固定头部的高度 */
  display: flex;
  flex-direction: column;
}

.input-section {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 20px;
  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

/* 隐藏 WebKit 浏览器的滚动条 */
.input-section::-webkit-scrollbar {
  display: none;
}

.right-panel {
  flex: 1;
  background: linear-gradient(135deg, #F8FAFF 0%, #F0F4FF 100%);
  padding: 24px;
  min-height: calc(100vh - 60px);
  overflow-y: auto;
  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

/* 隐藏 WebKit 浏览器的滚动条 */
.right-panel::-webkit-scrollbar {
  display: none;
}

/* 左侧输入区域样式 */
.input-group {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
}

.textarea-wrapper {
  position: relative;
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #999;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
  pointer-events: none;
}

.brand-input,
.slogan-input {
  width: 100%;
  padding: 12px 16px;
  
  border-radius: 5px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.brand-input:focus,
.slogan-input:focus {
  outline: none;
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
}

.industry-selection {
  margin-top: 8px;
}

.industry-tags {
  display: flex;
  flex-wrap: nowrap;
  display: grid;
  grid-template-columns: auto auto;
  gap: 7px 13px;
}

.industry-tag {
  display: flex;
  align-items: center;
  gap: 6px;
    padding: 12px 4px;
    /* border: 1px solid #e1e5e9; */
  border-radius: 5px;
    font-size: 11px;
  cursor: pointer;
    transition: all 0.3sease;
  background: white;
    flex: 1;
    min-width: 0;
    justify-content: center;
}

.industry-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.industry-tag:hover {
  border-color: #4285f4;
  background: #f8f9ff;
}

.industry-tag.active {
  border: 1px solid #005DFF;
}

.industry-tag.more-btn {
  background: #f0f2f5;
  color: #666;
  border: 1px solid #e1e5e9;
  border-radius: 5px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.industry-tag.more-btn:hover {
  background: #e1e5e9;
  border-color: #4285f4;
}

.advanced-settings {
  margin-top: 32px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.setting-group {
  margin-bottom: 20px;
}

.setting-tabs {
  display: flex;
  gap: 8px;
}

.tab-btn {
  flex: 1;
  padding: 8px 16px;
  border: 1px solid #e1e5e9;
  background: white;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn.active {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

.setting-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.setting-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.generate-desc-btn {
  padding: 6px 12px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.generate-desc-btn:hover:not(:disabled) {
  background: #3367d6;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(66, 133, 244, 0.3);
}

.generate-desc-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.generating-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-loading-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

.generate-desc-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.generate-desc-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex-shrink: 0;
}

.description-textarea {
  width: 100%;
  padding: 12px 16px;
  
  border-radius: 8px;
  font-size: 13px;
  resize: vertical;
  min-height: 80px;
}

.description-textarea:focus {
  outline: none;
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
}

.style-options {
  display: flex;
  gap: 16px;
}

.style-input-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.generate-btn {
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient( 308deg, #005DFF 0%, #91BAFF 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 20px 0 0 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
}

.generate-btn-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* 统一图标大小控制 */
.ai-logo-close-btn svg {
  width: 18px;
  height: 18px;
}

.ai-logo-nav-btn svg {
  width: 20px;
  height: 20px;
}

.modal-close svg {
  width: 16px;
  height: 16px;
}

.check-icon svg {
  width: 12px;
  height: 12px;
}

.industry-tag svg,
.toolbar-btn svg {
  width: 14px;
  height: 14px;
}

.generate-btn-text {
  font-size: 16px;
  font-weight: 600;
}

.generate-btn-cost {
  font-size: 12px;
  font-weight: 400;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 4px;
}

.generate-btn:hover:not(:disabled) {
  background: #3367d6;
}

.generate-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 免责协议样式 */
.disclaimer {
  margin-top: 3px;
  padding: 4px;
  text-align: center;
}

.disclaimer-link {
  color: #999999;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.disclaimer-link:hover {
  text-decoration: underline;
}

.disclaimer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid #999999;
  color: #999999;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
}

/* 免责声明弹窗样式 */
.disclaimer-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.disclaimer-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.disclaimer-content {
  line-height: 1.6;
}

.disclaimer-content h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 16px 0 8px 0;
}

.disclaimer-content p {
  font-size: 13px;
  color: #666;
  margin: 8px 0;
}

.disclaimer-content ul {
  margin: 8px 0;
  padding-left: 20px;
}

.disclaimer-content li {
  font-size: 13px;
  color: #666;
  margin: 4px 0;
}

.disclaimer-footer {
  font-weight: 500;
  color: #333;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e1e5e9;
}

.model-selection {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e1e5e9;
}

.model-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  
  gap: 8px;
}

.model-card {
  position: relative;
  padding: 12px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.model-card:hover {
  border-color: #4285f4;
  background: #f8f9ff;
}

.model-card.active {
  border-color: #4285f4;
  background: #f8f9ff;
}

.model-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #ddd;
}

.model-card.disabled:hover {
  transform: none;
  box-shadow: none;
}

.model-badge {
  position: absolute;
  top: -6px;
  right: 8px;
  padding: 2px 6px;
  font-size: 9px;
  border-radius: 3px;
  color: white;
}

.model-badge.recommended {
  background: #ff6b35;
}

.model-badge.featured {
  background: #4285f4;
}

.model-badge.coming-soon {
  background: #9E9E9E;
}

.model-name {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.model-desc {
  font-size: 10px;
  color: #666;
  margin: 0;
  line-height: 1.3;
}

/* 右侧展示区域样式 */
.showcase-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
}

.loading-text {
  font-size: 14px;
  color: #666;
  font-weight: 400;
}

.showcase-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.continue-btn {
  padding: 8px 16px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.continue-btn:hover:not(:disabled) {
  background: #3367d6;
}

.continue-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.clear-btn {
  padding: 8px 16px;
  background: #999999; /* Red color for clear button */
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.clear-btn:hover:not(:disabled) {
  background: #d32f2f; /* Darker red on hover */
}

.clear-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.regenerate-btn {
  padding: 8px 16px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.regenerate-btn:hover:not(:disabled) {
  background: #3367d6;
}

.regenerate-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.category-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.category-tab {
  padding: 8px 16px;
border-radius: 5px;
  background: #F2F7F7;
    border: none;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.category-tab:hover {
  border-color: #4285f4;
  background: #f8f9ff;
}

.category-tab.active {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

.category-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.logo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 20px;
}

.logo-item {

  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  background:#F2F2F2;
  border-radius: 5px;

}

.logo-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.logo-image-wrapper {
  position: relative;
  aspect-ratio: 4/3;
  overflow: hidden;
  background: white;
  border-radius: 5px;
}

.logo-image-wrapper.has-watermark::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('@/assets/static/images/水印图.png');
  background-repeat: no-repeat;
  background-size: 500px 400px;
  background-position: 50px 0px;
  opacity: 0.7;
  pointer-events: none;
  z-index: 2;
  transform: rotate(45deg) scale(1.3);
}

.logo-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 1;
}

/* AI生成标签样式 */
.ai-generated-tag {
  position: absolute;
  bottom: 8px;
  right: 8px;

  padding: 4px 8px;

  z-index: 5;
}

.ai-tag-text {
  font-size: 10px;
  font-weight: 600;
  background: #999999;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.5px;
}

/* 工具栏样式 - 悬停时显示在图片底部并填满 */
.logo-toolbar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0;
  padding: 12px 0;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 0 0 5px 5px;
  z-index: 3;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.logo-item:hover .logo-toolbar {
  opacity: 1;
  visibility: visible;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
  border-radius: 0;
  flex: 1;
  position: relative;
}

.toolbar-btn:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.3);
}

.toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.toolbar-btn i {
  font-size: 14px;
}

.toolbar-btn span {
  font-size: 12px;
  font-weight: 500;
}

.edit-btn:hover {
  color: #ff9800;
  background: rgba(255, 152, 0, 0.3);
}

.preview-btn:hover {
  color: #2196f3;
  background: rgba(33, 150, 243, 0.3);
}

.download-btn:hover {
  color: #4caf50;
  background: rgba(76, 175, 80, 0.3);
}

/* 移除原来的覆盖层样式 */
.logo-overlay {
  display: none;
}

/* 新的logo信息区域样式 */
.logo-info-section {
  padding: 16px;
  background: #F2F7F7;
  border: 1px solid #EBEBEB;
  border-radius: 0px 0px 5px 5px;
  border-top: none;
}

.logo-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  line-height: 1.3;
  cursor: help;
  transition: color 0.2s ease;
  /* 限制为一行显示 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.logo-title:hover {
  color: #4285f4;
}

.logo-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.logo-tag {
  padding: 3px 8px;
  background: #f0f2f5;
  color: #666;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.logo-prompt {
  margin-bottom: 8px;
}

.prompt-text {
  font-size: 12px;
  line-height: 1.4;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logo-process-info {
  margin-top: 8px;
}

.process-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.process-tag {
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 500;
  color: white;
}

.process-tag.bg-removed {
  background: rgba(52, 168, 83, 0.8);
}

.process-tag.svg-converted {
  background: rgba(255, 107, 53, 0.8);
}

.process-tag.template-generated {
  background: rgba(156, 39, 176, 0.8);
}

/* 加载卡片样式 */
.loading-item {
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fa;
 
}

.loading-item .logo-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(308deg, #005DFF 0%, #91BAFF 100%);
}

.loading-placeholder {
  text-align: center;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 14px;
  color: white;
  margin: 0;
  font-weight: 500;
}

.loading-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.progress-bar-mini {
  width: 120px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill-mini {
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text-mini {
  font-size: 12px;
  color: white;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}


/* 响应式设计 */
@media (max-width: 1024px) {
  .main-container {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .main-container {
    padding: 16px;
  }
  
  .left-panel,
  .right-panel {
    padding: 16px;
  }
  
  .logo-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
  
  .category-tabs {
    flex-wrap: wrap;
  }
  
  .generate-btn {
    flex-direction: column;
    gap: 4px;
    padding: 16px 24px;
  }
  
  .generate-btn-icon {
    width: 16px;
    height: 16px;
  }
  
  .generate-btn-text {
    font-size: 14px;
  }
  
  .generate-btn-cost {
    font-size: 11px;
    margin-left: 0;
  }
}

/* 行业选择悬浮窗样式 */
.industry-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.industry-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e1e5e9;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.modal-close:hover {
  background: #e1e5e9;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.industry-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
}

.industry-item {
  position: relative;
  padding: 16px 12px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.industry-item:hover {
  border-color: #4285f4;
  background: #f8f9ff;
}

.industry-item.active {
  border-color: #4285f4;
  background: #f8f9ff;
}

.industry-item .industry-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.industry-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.industry-name {
  font-size: 12px;
  color: #333;
  line-height: 1.3;
}

.check-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
  background: #4285f4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e1e5e9;
  display: flex;
  justify-content: center;
}

.confirm-btn {
  padding: 12px 32px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.confirm-btn:hover {
  background: #3367d6;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .industry-modal {
    width: 95%;
    max-height: 85vh;
  }
  
  .industry-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
  
  .modal-header,
  .modal-content,
  .modal-footer {
    padding: 16px;
  }
}

.style-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.style-item {
  cursor: pointer;
  transition: all 0.3s ease;
}

.style-wrap {
  width: 78px;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.style-item:hover .style-wrap {
  border-color: #4285f4;
  transform: translateY(-2px);
}

.style-item.active .style-wrap {
  border-color: #4285f4;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
}

.style-wrap img {
  width: 100%;
  height: 60px;
  object-fit: cover;
  display: block;
}

.style-name {
  padding: 8px 4px;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  color: #333;
  background: white;
  line-height: 1.2;
}

.style-item.active .style-name {
  color: #4285f4;
  font-weight: 600;
}

/* 生成数量选择样式 */
.count-selection {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.count-option {
  padding: 8px 16px;
 
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.count-option:hover:not(.active) {
  border-color: #4285f4;
  background: #f8f9ff;
}

.count-option.active {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

/* 设计方案选择弹窗样式 */
.solution-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.solution-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.solution-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e1e5e9;
}

.solution-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.solution-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: color 0.3s ease;
}

.solution-modal-close:hover {
  color: #333;
}

.solution-modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.solution-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.solution-item {
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.solution-item:hover {
  border-color: #4285f4;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.1);
}

.solution-item.active {
  border-color: #4285f4;
  background: #f8f9ff;
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.15);
}

.solution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.solution-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.solution-badge {
  background: #4285f4;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.solution-description {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.solution-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-right: 8px;
}

.solution-colors,
.solution-graphics,
.solution-typography,
.solution-elements {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
}

.color-tags,
.elements-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.color-tag,
.element-tag {
  padding: 4px 10px;
  background: #f0f2f5;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
}

.graphics-list {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.graphics-list li {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 4px 0;
}

.typography-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.typography-info span {
  font-size: 13px;
  color: #666;
}

.solution-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e1e5e9;
}

.solution-btn-cancel,
.solution-btn-confirm {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.solution-btn-cancel {
  background: #f0f2f5;
  color: #666;
}

.solution-btn-cancel:hover {
  background: #e1e5e9;
}

.solution-btn-confirm {
  background: #4285f4;
  color: white;
}

.solution-btn-confirm:hover {
  background: #3367d6;
}
</style> 